import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  ChangeDetectorRef,
  NgZone,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { EducationalPathDTO } from '../../../dto/educational-path/educational-path.dto';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { Observable, Subject, of } from 'rxjs';
import { map, tap, takeUntil, filter } from 'rxjs/operators';
import { EducationalPathVersionDTO } from '../../../dto/educational-path/educational-path-version.dto';
import { Router, NavigationEnd } from '@angular/router';
import {
  boundsFromApiWindow,
  computeMaintenanceRollBounds,
  formatRollYearsLabel,
  isMultiYearRoll,
  versionToRollInput,
} from '../../../utils/maintenance-roll-bounds.util';

interface PathResponse {
  path: EducationalPathDTO;
  user_paths?: any[];
}

@Component({
  selector: 'app-duna-educational-path-info',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './duna-educational-path-info.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-educational-path-info.component.scss',
})
export class DunaEducationalPathInfoComponent implements OnInit, OnDestroy {
  @Input() selectedVersionId: number | null = null;
  educationalPath$: Observable<EducationalPathDTO | null> = of(null);
  private destroy$ = new Subject<void>();
  private lastPathGuid: string | null = null;

  constructor(
    private componentDataSourceService: ComponentDataSourceService,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone
  ) {
    // Clear cache and initialize data
    this.initializePathData();

    // Subscribe to route changes
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const currentGuid = this.componentDataSourceService.getCurrentSlug();

        // If the GUID has changed, clear cache and reload data
        if (currentGuid !== this.lastPathGuid) {
          this.initializePathData();
        }
      });

    // Subscribe to version ID changes from the service
    this.componentDataSourceService
      .getCurrentVersionId$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((versionId) => {
        if (versionId) {
          this.selectedVersionId = parseInt(versionId, 10);
          // Mark for change detection within NgZone to avoid assertion errors
          this.ngZone.run(() => {
            this.changeDetectorRef.markForCheck();
          });
        }
      });
  }

  private initializePathData(): void {
    // Clear the cache
    this.componentDataSourceService.clearEducationalPathData();

    // Store the current path GUID
    this.lastPathGuid = this.componentDataSourceService.getCurrentSlug();

    // Initialize the observable
    this.educationalPath$ = this.componentDataSourceService
      .getEducationalPathData()
      .pipe(
        tap((response) => {
        }),
        map((response: PathResponse | EducationalPathDTO | null) => {
          // Check if response has a nested 'path' property
          if (response && typeof response === 'object' && 'path' in response) {
            return response.path;
          }
          return response as EducationalPathDTO | null;
        })
      );
  }

  ngOnInit() {
    // If no version is selected, try to get the version ID from the URL or other source
    if (!this.selectedVersionId) {
      this.selectedVersionId =
        this.componentDataSourceService.getCurrentVersionId()
          ? parseInt(
              this.componentDataSourceService.getCurrentVersionId() as string,
              10
            )
          : null;
    }

    // Also initialize from the service's current value
    const currentVersionId =
      this.componentDataSourceService.getCurrentVersionId();
    if (currentVersionId && !this.selectedVersionId) {
      this.selectedVersionId = parseInt(currentVersionId, 10);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getSelectedVersion(
    path: EducationalPathDTO
  ): EducationalPathVersionDTO | null {
    if (!path || !path.versions || path.versions.length === 0) {
      return null;
    }

    if (this.selectedVersionId) {
      const selectedVersion = path.versions.find(
        (v) => v.version_id === this.selectedVersionId
      );
      if (selectedVersion) {
        return selectedVersion;
      }
    }

    // If no version is selected or found, return null - don't default to the latest
    return null;
  }

  hasSelectedVersion(): boolean {
    return this.selectedVersionId !== null;
  }

  getMaintenanceIntervalText(path: EducationalPathDTO): string {
    const version = this.getSelectedVersion(path);
    if (
      !version ||
      version.recurrence_value === null ||
      version.recurrence_unit === null
    ) {
      return '-';
    }

    const value = version.recurrence_value;
    const unit = version.recurrence_unit.toLowerCase();
    const recurrenceType = version.recurrence_type || 'calendar';

    let intervalText = '';

    // Handle full period
    if (unit === 'full_period') {
      intervalText = 'Hele perioden';
    }
    // Handle years - check both 'year'/'years' units and day values that are multiples of 365
    else if (unit === 'year' || unit === 'years') {
      if (value === 1) {
        intervalText = 'Årligt';
      } else {
        intervalText = `Hver ${value} år`;
      }
    }
    // Handle days that represent years (multiples of 365)
    else if (unit === 'day' || unit === 'days') {
      if (value % 365 === 0 && value >= 365) {
        const years = value / 365;
        if (years === 1) {
          intervalText = 'Årligt';
        } else {
          intervalText = `Hver ${years} år`;
        }
      } else if (value % 30 === 0 && value >= 30 && value < 365) {
        // Handle days that represent months
        const months = value / 30;
        if (months === 1) {
          intervalText = 'Månedligt';
        } else if (months === 6) {
          intervalText = 'Hver 6. måned';
        } else if (months === 3) {
          intervalText = 'Hver 3. måned';
        } else {
          intervalText = `Hver ${months} måneder`;
        }
      } else {
        intervalText = `Hver ${value} dag${value > 1 ? 'e' : ''}`;
      }
    }
    // Handle months
    else if (unit === 'month' || unit === 'months') {
      if (value === 1) {
        intervalText = 'Månedligt';
      } else if (value === 6) {
        intervalText = 'Hver 6. måned';
      } else if (value === 3) {
        intervalText = 'Hver 3. måned';
      } else {
        intervalText = `Hver ${value} måneder`;
      }
    }
    // Handle weeks
    else if (unit === 'week' || unit === 'weeks') {
      if (value === 1) {
        intervalText = 'Ugentligt';
      } else {
        intervalText = `Hver ${value} uger`;
      }
    }
    // Fallback for unknown units
    else {
      intervalText = `${value} ${unit}`;
    }

    // Add recurrence type indicator
    if (recurrenceType === 'per_user') {
      intervalText += ' (per bruger)';
    }

    return intervalText;
  }

  getRecurrenceTypeText(path: EducationalPathDTO): string {
    const version = this.getSelectedVersion(path);
    if (!version || !version.recurrence_type) {
      return '';
    }

    return version.recurrence_type === 'per_user'
      ? 'Per bruger (fra færdiggørelse)'
      : 'Kalenderbaseret';
  }

  getRequirementsCount(path: EducationalPathDTO): number {
    const version = this.getSelectedVersion(path);
    return version && version.requirements ? version.requirements.length : 0;
  }

  getMaintenanceCount(path: EducationalPathDTO): number {
    const version = this.getSelectedVersion(path);
    return version && version.maintenance ? version.maintenance.length : 0;
  }

  getMaintenanceMinutesRequired(path: EducationalPathDTO): number {
    const version = this.getSelectedVersion(path);
    if (!version) return 0;

    // First check maintenance_minutes_required, then fall back to required_maintenance_minutes
    if (version.maintenance_minutes_required) {
      return version.maintenance_minutes_required;
    } else if (version.required_maintenance_minutes) {
      return version.required_maintenance_minutes;
    }
    return 0;
  }

  formatMinutesToHours(minutes: number): string {
    if (minutes === 0) return '0 timer';
    const hours = minutes / 60;
    return `${hours.toFixed(1)} timer`;
  }

  getCurrentMaintenancePeriod(path: EducationalPathDTO): {
    start: Date | null;
    end: Date | null;
  } {
    const version = this.getSelectedVersion(path);
    if (!version) {
      return { start: null, end: null };
    }

    const fromApi = boundsFromApiWindow(
      version.recurrence_window_start,
      version.recurrence_window_end
    );
    if (fromApi) {
      return { start: fromApi.start, end: fromApi.end };
    }

    const computed = computeMaintenanceRollBounds(
      versionToRollInput(version),
      new Date()
    );
    if (!computed) {
      return { start: null, end: null };
    }
    return { start: computed.start, end: computed.end };
  }

  getCalendarAnchorDateText(path: EducationalPathDTO): string | null {
    const version = this.getSelectedVersion(path);
    const raw = version?.recurrence_calendar_anchor_date;
    if (!raw) return null;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString('da-DK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  getRollYearsLabel(path: EducationalPathDTO): string | null {
    const version = this.getSelectedVersion(path);
    if (!version) return null;
    if (
      !isMultiYearRoll(version.recurrence_value, version.recurrence_unit)
    ) {
      return null;
    }
    const period = this.getCurrentMaintenancePeriod(path);
    if (!period.start || !period.end) return null;
    return formatRollYearsLabel({ start: period.start, end: period.end });
  }

  getRemainingDaysInMaintenancePeriod(path: EducationalPathDTO): number | null {
    const period = this.getCurrentMaintenancePeriod(path);
    if (!period.end) return null;

    const today = new Date();
    const end = new Date(period.end);
    const diffTime = Math.abs(end.getTime() - today.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  }

  getVersionName(path: EducationalPathDTO): string {
    const version = this.getSelectedVersion(path);
    return version ? version.name : '-';
  }

  getVersionId(path: EducationalPathDTO): number | null {
    const version = this.getSelectedVersion(path);
    return version ? version.version_id : null;
  }

  getVersionCreatedAt(path: EducationalPathDTO): Date | null {
    const version = this.getSelectedVersion(path);
    return version && version.created_at ? new Date(version.created_at) : null;
  }

  getOrganizationId(path: EducationalPathDTO): number | null {
    return path.organizations_id;
  }
}
